import React, { useCallback, useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { ListingResource, PropertyField } from '../../../system/field';
import {
  ResourceField,
  useResourceForm,
} from '../../edit/hooks/useResourceForm/useResourceForm';
import { FieldType } from '../../../system/field-type';
import CustomTooltip from '../CustomTooltip';
import { MemoizedFieldContextProps } from '../../../pages/edit/components/MemoizedField';
import { ValidationRule } from '@brightmls/nextgen.lae.flc';

const ComplianceTooltip: React.FC<{ text: string }> = ({ text }) => {
  return (
    <CustomTooltip
      title={
        <>
          <Typography sx={{ fontSize: 12 }}>
            Bright Rules and Regulations Section 1.2 states that all listing
            information must be entered completely and accurately to avoid
            sanctions.
          </Typography>
        </>
      }
      placement="bottom"
    >
      <Button
        href="#"
        component={Link}
        sx={{
          color: 'secondary.medium',
          display: 'inline',
          fontSize: 14,
          fontWeight: '400',
          p: 0,
          textDecoration: 'underline',
          textTransform: 'capitalize',
        }}
      >
        {text}
      </Button>
    </CustomTooltip>
  );
};

export const NoTaxRecordHeader: React.FC = () => {
  return (
    <>
      <Box sx={{ marginTop: 0, alignItems: 'center', p: 0 }}>
        <Typography
          sx={{ fontWeight: '400', fontSize: 14, color: '#1A1A1E' }}
          variant="body1"
          className="bmls_typography"
          component="p"
        >
          Consider searching for a tax record before you continue entering your
          listing. Remember, entering an address that already has a tax record
          may result in a <ComplianceTooltip text="Compliance Violation" />.
        </Typography>
      </Box>
    </>
  );
};

export const NoTaxRecordFormLink: React.FC<{ onSave: Function }> = ({
  onSave,
}) => {
  const LATLONG_KEY = 'LATLONG_DATA';
  const { openResourceForm } = useResourceForm();
  const [formData, setFormData] = useState<any>({});

  const getValidationRules = useCallback(
    (propertyKey?: PropertyField): ValidationRule[] => {
      return [
        {
          /**
           * Validates and controls the disabled state of address input fields based on latitude/longitude enablement
           * and dependent field values.
           *
           * @param value - The current value of the field being validated
           * @param formValues - All form values from the current form state
           * @returns An object with { disabled: true } to disable the field, or undefined to keep it enabled
           *
           * @example
           * // When lat/long is enabled, all address fields are disabled
           * // formValues[LATLONG_KEY]?.IS_ENABLED = true
           * // Returns: { disabled: true }
           *
           * @example
           * // For dependent fields (e.g., county depends on state)
           * // If propertyKey = PropertyField.STATE_OR_PROVINCE and it's empty
           * // Returns: { disabled: true } for county field
           *
           * @remarks
           * This validation serves two main purposes:
           * 1. When latitude/longitude input is enabled, disable all manual address input fields
           *    to prevent conflicting data entry methods
           * 2. Create field dependencies where certain fields (like county, postal code) are disabled
           *    until their prerequisite fields (like state, county respectively) are filled
           */
          validate: (value: any, formValues: any) => {
            // If latitude/longitude input is enabled, disable all address fields
            // This prevents users from manually entering address data when using coordinates
            if (formValues[LATLONG_KEY]?.IS_ENABLED) return { disabled: true };

            // If a dependency field is specified, check if it has a value
            // This creates a cascade effect: state -> county -> postal fields
            // Fields remain disabled until their prerequisite field is populated
            if (propertyKey && !formValues[propertyKey]) {
              return { disabled: true };
            }

            // No validation error - field remains enabled
            return undefined;
          },
        },
      ];
    },
    [],
  );

  const ProceedWithNoTaxFields: ResourceField[] = [
    {
      Key: LATLONG_KEY,
      Label: '',
      Layout: 'single',
      Control: 'LatLongCheckbox',
      Metadata: {
        Key: LATLONG_KEY,
        Type: FieldType.BOOL,
      },
      validationRules: [
        {
          validate: (value: any, formValues: any) => {
            const latLngValues = formValues[LATLONG_KEY];
            if (
              latLngValues?.IS_ENABLED &&
              (!latLngValues.LAT || !latLngValues.LONG)
            ) {
              return { error: 'Missing values' };
            }
            return undefined;
          },
        },
      ],
    },
    {
      Key: PropertyField.STREET_NUMBER,
      Label: 'Street Number',
      Layout: 'single',
      validationRules: getValidationRules(),
    },
    {
      Key: PropertyField.STREET_NAME,
      Label: 'Street Name',
      Layout: 'single',
      ForceRequired: true,
      SkipRequiredIfReadOnly: true,
      validationRules: getValidationRules(),
    },
    {
      Key: PropertyField.STREET_SUFFIX,
      Label: 'Street Suffix',
      Layout: 'single',
      validationRules: getValidationRules(PropertyField.STREET_NAME),
    },
    {
      Key: PropertyField.STREET_DIR_SUFFIX,
      Label: 'Street Direction Suffix',
      Layout: 'single',
      validationRules: getValidationRules(PropertyField.STREET_NAME),
    },
    {
      Key: PropertyField.UNIT_NUMBER,
      Label: 'Unit Number',
      Layout: 'single',
      validationRules: getValidationRules(),
    },
    {
      Key: PropertyField.STATE_OR_PROVINCE,
      Label: 'State or Province',
      Layout: 'single',
      ForceRequired: true,
      SkipRequiredIfReadOnly: true,
      validationRules: getValidationRules(),
      onFieldChange: (value: any, context: MemoizedFieldContextProps) => {
        context.updateField(PropertyField.COUNTY, '');
      },
    },
    {
      Key: PropertyField.COUNTY,
      Label: 'County',
      Layout: 'single',
      ForceRequired: true,
      SkipRequiredIfReadOnly: true,
      Control: 'SearchCounty',
      passResourceFormContext: true,
      validationRules: getValidationRules(PropertyField.STATE_OR_PROVINCE),
    },
    {
      Key: PropertyField.CITY,
      Label: 'Postal City',
      Layout: 'single',
      ForceRequired: true,
      SkipRequiredIfReadOnly: true,
      Control: 'SearchPostalCity',
      passResourceFormContext: true,
      validationRules: getValidationRules(PropertyField.COUNTY),
    },
    {
      Key: PropertyField.POSTAL_CODE,
      Label: 'Zip Code',
      Layout: 'single',
      ForceRequired: true,
      SkipRequiredIfReadOnly: true,
      Control: 'SearchPostalCode',
      passResourceFormContext: true,
      validationRules: getValidationRules(PropertyField.COUNTY),
    },
  ];

  const updateLocationData = useCallback(
    (newData: any) => {
      setFormData((prevState: any) => ({
        ...prevState,
        ...newData,
      }));
    },
    [setFormData],
  );

  const handleProceedWithNoTaxRecord = () => {
    openResourceForm(
      {
        fields: ProceedWithNoTaxFields,
        resourceType: ListingResource.Property,
        resourceIndex: 0,
        internalResourceManagement: true,
        formHeader: <NoTaxRecordHeader />,
        formStyles: {
          '.FormFieldLabel-readOnly + .MuiAutocomplete-root': {
            '[readonly]': {
              color: 'grey.400',
            },
          },
          '.MuiInputBase-readOnly, .FormFieldLabel-readOnly': {
            color: 'grey.400',
            background: 'common.white',
            '.MuiTypography-root': {
              color: 'grey.400',
            },
          },
          '.MuiInputBase-root.Mui-readOnly, .MuiAutocomplete-inputRoot.MuiOutlinedInput-root.Mui-readOnly':
            {
              backgroundColor: 'common.white',
              '.MuiTypography-root': {
                color: 'grey.400',
              },
            },
        },
      },
      {
        title: 'No Tax Record',
      },
      (record: any, isCreate: boolean) => {
        updateLocationData(record);
        onSave(record);
      },
      { ...formData },
    );
  };

  return (
    <Button variant="text" onClick={() => handleProceedWithNoTaxRecord()}>
      <Typography
        children={'Or Proceed Without Tax Record'}
        style={{
          textTransform: 'capitalize',
          fontSize: '14px',
          color: '#253772',
          fontWeight: '500',
        }}
      />
    </Button>
  );
};
